import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus, Trash2, Loader2, Save, ArrowUp, ArrowDown, Eye, EyeOff,
  Type, Video, Images, Megaphone, Layers, Home as HomeIcon,
  Award, Sparkles, Newspaper, Upload,
} from 'lucide-react';
import { getHomeLayout, saveHomeLayout, uploadFile, resolveFileUrl } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

const BUILT_INS = {
  hero: { label: 'Hero + Market Headlines', icon: HomeIcon, locked: true },
  stats: { label: 'Stats (Countries, Verticals…)', icon: Award, locked: false },
  mission_vision: { label: 'Mission & Vision', icon: Sparkles, locked: false },
  services: { label: 'Business Verticals (scrollable)', icon: Layers, locked: false },
  newsroom: { label: 'Newsroom (latest press)', icon: Newspaper, locked: false },
};

const CUSTOM_TYPES = [
  { key: 'text_block', label: 'Text Block', icon: Type, desc: 'Eyebrow + heading + rich body' },
  { key: 'featured_video', label: 'Featured Video', icon: Video, desc: 'YouTube or Vimeo embed with title' },
  { key: 'image_strip', label: 'Image Strip', icon: Images, desc: 'Grid of images with captions' },
  { key: 'cta_banner', label: 'CTA Banner', icon: Megaphone, desc: 'Dark banner with call-to-action button' },
];

const defaultConfig = (type) => {
  switch (type) {
    case 'text_block':
      return { eyebrow: '', heading: 'New section', body: '', align: 'center' };
    case 'featured_video':
      return { title: 'Featured', subtitle: '', video_url: '' };
    case 'image_strip':
      return { title: 'Gallery', images: [{ url: '', caption: '' }] };
    case 'cta_banner':
      return { title: 'Ready to build together?', body: '', cta_label: 'Contact us', cta_href: '/contact', image_url: '' };
    default:
      return {};
  }
};

const HomeLayoutAdminPage = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getHomeLayout();
      setSections(data.sections || []);
    } catch {
      toast({ title: 'Failed to load layout', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = (idx, dir) => {
    setSections((arr) => {
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const toggle = (idx) => setSections((arr) => arr.map((s, i) => i === idx ? { ...s, enabled: !s.enabled } : s));
  const remove = (idx) => setSections((arr) => arr.filter((_, i) => i !== idx));
  const patchConfig = (idx, patch) => setSections((arr) => arr.map((s, i) => i === idx ? { ...s, config: { ...(s.config || {}), ...patch } } : s));

  const addBlock = (type) => {
    setSections((arr) => [...arr, { id: uid(), type, enabled: true, config: defaultConfig(type) }]);
    setShowAdd(false);
  };

  const uploadInto = async (file, cb) => {
    if (!file) return;
    try {
      const r = await uploadFile(file);
      cb(r.url);
      toast({ title: 'Image uploaded' });
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { sections: sections.map((s, i) => ({ ...s, order: (i + 1) * 10 })) };
      await saveHomeLayout(payload);
      toast({ title: 'Homepage saved' });
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-black/5 p-10 text-center text-[#0b1a2e]/60">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-home-layout">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">CMS</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Homepage Layout
          </h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">
            Toggle, reorder or add custom blocks that appear on the public homepage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-block-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#0f5c3d]/25 text-sm font-medium text-[#0f5c3d] hover:bg-[#f7fbf8]"
          >
            <Plus className="w-4 h-4" /> Add block
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-home-layout-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {sections.map((s, idx) => {
          const isBuiltIn = !!BUILT_INS[s.type];
          const info = isBuiltIn
            ? BUILT_INS[s.type]
            : CUSTOM_TYPES.find((c) => c.key === s.type);
          const Icon = info?.icon || Layers;
          return (
            <div
              key={s.id}
              data-testid="home-section-row"
              className={`bg-white border border-black/5 rounded-2xl overflow-hidden ${s.enabled ? '' : 'opacity-60'}`}
            >
              <div className="flex items-center gap-3 p-4 border-b border-black/5">
                <div className="w-10 h-10 rounded-lg bg-[#f7fbf8] flex items-center justify-center text-[#0f5c3d]">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0b1a2e]">
                    {info?.label || s.type}
                    {isBuiltIn && (
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/50">Built-in</span>
                    )}
                  </div>
                  <div className="text-xs text-[#0b1a2e]/50">Position {idx + 1}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-2 rounded-lg hover:bg-black/5 disabled:opacity-30" title="Move up"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === sections.length - 1} className="p-2 rounded-lg hover:bg-black/5 disabled:opacity-30" title="Move down"><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => toggle(idx)} title={s.enabled ? 'Hide' : 'Show'} className="p-2 rounded-lg hover:bg-black/5">
                    {s.enabled ? <Eye className="w-4 h-4 text-[#0f5c3d]" /> : <EyeOff className="w-4 h-4 text-red-500" />}
                  </button>
                  {!isBuiltIn && (
                    <button onClick={() => remove(idx)} title="Delete" className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
              {!isBuiltIn && (
                <div className="p-4 space-y-3 bg-[#f7fbf8]/40">
                  <BlockConfigEditor
                    section={s}
                    onChange={(patch) => patchConfig(idx, patch)}
                    onUpload={uploadInto}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add block modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAdd(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-xl font-semibold text-[#0b1a2e] mb-1">Add a custom block</h3>
            <p className="text-sm text-[#0b1a2e]/60 mb-5">Choose a block type — you can edit its content below after adding.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CUSTOM_TYPES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => addBlock(c.key)}
                  data-testid={`add-block-${c.key}`}
                  className="text-left p-4 rounded-xl border border-[#0f5c3d]/15 hover:border-[#0f5c3d] hover:bg-[#f7fbf8] transition"
                >
                  <c.icon className="w-5 h-5 text-[#0f5c3d] mb-2" />
                  <div className="font-medium text-[#0b1a2e]">{c.label}</div>
                  <div className="text-xs text-[#0b1a2e]/60 mt-1">{c.desc}</div>
                </button>
              ))}
            </div>
            <div className="mt-4 text-right">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-lg text-sm text-[#0b1a2e]/70 hover:bg-black/5">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">{label}</span>
    {children}
  </label>
);

const Input = (props) => (
  <input {...props} className={`w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 ${props.className || ''}`} />
);

const Textarea = (props) => (
  <textarea {...props} className={`w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 resize-y ${props.className || ''}`} />
);

const ImagePicker = ({ value, onChange, onUpload, small = false }) => (
  <div className={`flex items-start gap-3 ${small ? '' : ''}`}>
    <div className={`${small ? 'w-16 h-16' : 'w-24 h-16'} rounded-lg overflow-hidden bg-[#e2ede6] flex items-center justify-center shrink-0`}>
      {value ? <img src={resolveFileUrl(value)} alt="" className="w-full h-full object-cover" /> : <Images className="w-4 h-4 text-[#0b1a2e]/30" />}
    </div>
    <div className="flex-1 space-y-1.5">
      <label className="inline-flex items-center gap-2 px-2 py-1.5 rounded-lg border border-[#0f5c3d]/20 text-xs cursor-pointer hover:bg-[#f7fbf8]">
        <Upload className="w-3 h-3" /> Upload
        <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0], onChange)} />
      </label>
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="or paste URL"
        className="w-full border border-[#0f5c3d]/15 rounded-lg px-2 py-1 text-[11px]"
      />
    </div>
  </div>
);

const BlockConfigEditor = ({ section, onChange, onUpload }) => {
  const c = section.config || {};
  if (section.type === 'text_block') {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Eyebrow"><Input value={c.eyebrow || ''} onChange={(e) => onChange({ eyebrow: e.target.value })} /></Field>
          <div className="sm:col-span-2"><Field label="Heading"><Input value={c.heading || ''} onChange={(e) => onChange({ heading: e.target.value })} /></Field></div>
        </div>
        <Field label="Body (markdown-lite)"><Textarea rows={4} value={c.body || ''} onChange={(e) => onChange({ body: e.target.value })} placeholder="## Heading&#10;- Bullet&#10;**bold**" /></Field>
        <Field label="Alignment">
          <select value={c.align || 'center'} onChange={(e) => onChange({ align: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </Field>
      </div>
    );
  }
  if (section.type === 'featured_video') {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title"><Input value={c.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></Field>
          <Field label="Subtitle"><Input value={c.subtitle || ''} onChange={(e) => onChange({ subtitle: e.target.value })} /></Field>
        </div>
        <Field label="Video URL (YouTube or Vimeo)"><Input value={c.video_url || ''} onChange={(e) => onChange({ video_url: e.target.value })} placeholder="https://youtube.com/watch?v=…" /></Field>
      </div>
    );
  }
  if (section.type === 'image_strip') {
    const images = c.images || [];
    return (
      <div className="space-y-3">
        <Field label="Section title"><Input value={c.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></Field>
        <div className="space-y-2">
          {images.map((img, i) => (
            <div key={img.url || `img-${i}`} className="flex items-start gap-3 bg-white rounded-lg p-3 border border-black/5">
              <div className="flex-1">
                <ImagePicker
                  value={img.url}
                  onChange={(url) => onChange({ images: images.map((x, j) => j === i ? { ...x, url } : x) })}
                  onUpload={onUpload}
                  small
                />
                <input
                  value={img.caption || ''}
                  onChange={(e) => onChange({ images: images.map((x, j) => j === i ? { ...x, caption: e.target.value } : x) })}
                  placeholder="Caption (optional)"
                  className="mt-2 w-full border border-[#0f5c3d]/15 rounded-lg px-2 py-1 text-xs"
                />
              </div>
              <button
                type="button"
                onClick={() => onChange({ images: images.filter((_, j) => j !== i) })}
                className="p-2 rounded-lg text-red-600 hover:bg-red-50"
              ><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => onChange({ images: [...images, { url: '', caption: '' }] })}
            className="text-xs font-medium text-[#0f5c3d] hover:underline"
          >+ Add image</button>
        </div>
      </div>
    );
  }
  if (section.type === 'cta_banner') {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title"><Input value={c.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></Field>
          <Field label="CTA button label"><Input value={c.cta_label || ''} onChange={(e) => onChange({ cta_label: e.target.value })} /></Field>
        </div>
        <Field label="Body"><Textarea rows={2} value={c.body || ''} onChange={(e) => onChange({ body: e.target.value })} /></Field>
        <Field label="CTA link (URL or /path)"><Input value={c.cta_href || ''} onChange={(e) => onChange({ cta_href: e.target.value })} placeholder="/contact or https://…" /></Field>
        <Field label="Background image">
          <ImagePicker value={c.image_url} onChange={(url) => onChange({ image_url: url })} onUpload={onUpload} />
        </Field>
      </div>
    );
  }
  return null;
};

export default HomeLayoutAdminPage;
